/**
 * HOOF Performance Crew buy UI — native Shopify cart/add + cart drawer.
 * Availability + option matching use Liquid-built variant index
 * (variant.available + variant.options aligned to product.options).
 * Defaults: Pure White / 3 pairs / M.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const SIZE_RANGES = {
  S: 'US M 6–8 / W 7–9',
  M: 'US M 8.5–10.5 / W 9.5–11.5',
  L: 'US M 11–13',
};

const DEFAULTS = {
  Color: 'Pure White',
  Size: 'M',
};

const UI_OPTION_KEYS = ['Color', 'Pack', 'Size'];

function normalizePack(value) {
  if (value == null) return '';
  const v = String(value).trim();
  const lower = v.toLowerCase();
  if (
    lower === '3 pack' ||
    lower === '3-pack' ||
    lower === '3 pairs' ||
    lower === '3 pair'
  ) {
    return '3 pairs';
  }
  if (lower === '1 pair' || lower === '1 pairs') return '1 Pair';
  return v;
}

function normalizeOptionValue(name, value) {
  if (isPackOptionName(name)) return normalizePack(value);
  return value == null ? '' : String(value).trim();
}

function isPackOptionName(name) {
  return String(name || '')
    .trim()
    .toLowerCase() === 'pack';
}

function money(cents, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/** Shopify money amounts are cents. compare_at only counts when greater than price. */
function saleMeta(variant) {
  if (!variant || variant.price == null) {
    return { onSale: false, compare: null, savePct: 0 };
  }
  const price = Number(variant.price);
  const compareRaw = variant.compare_at_price;
  const compare =
    compareRaw == null || compareRaw === '' ? null : Number(compareRaw);
  const onSale =
    Number.isFinite(compare) && Number.isFinite(price) && compare > price;
  const savePct = onSale
    ? Math.round(((compare - price) / compare) * 100)
    : 0;
  return { onSale, compare: onSale ? compare : null, savePct };
}

/** Resolve option names from string[] or { name, values }[]. */
function optionNamesFrom(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry.name === 'string') return entry.name;
      return null;
    })
    .filter(Boolean);
}

/** Shopify storefront truth: variant.available from Liquid. */
function isVariantAvailable(variant) {
  if (!variant) return false;
  if (typeof variant.available === 'boolean') return variant.available;
  if (typeof variant.availableForSale === 'boolean') return variant.availableForSale;
  if (variant.available === 'true' || variant.available === 1) return true;
  if (variant.available === 'false' || variant.available === 0) return false;
  return false;
}

/**
 * Map UI keys (Color/Pack/Size) → actual product option names.
 * Uses case-insensitive name match; does not assume option1/2/3 order.
 */
function buildOptionKeyMap(names) {
  const map = {};
  UI_OPTION_KEYS.forEach((uiKey) => {
    const found = names.find(
      (n) => String(n).trim().toLowerCase() === uiKey.toLowerCase()
    );
    map[uiKey] = found || uiKey;
  });
  return map;
}

class HoofProductBuy extends HTMLElement {
  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;

    const index = this.#parseVariantIndex();
    const productRaw = this.#parseProductJson();

    if (index?.variants?.length) {
      this.optionNames = optionNamesFrom(index.options);
      this.variants = index.variants.map((v) => ({
        id: v.id,
        available: Boolean(v.available),
        price: v.price,
        compare_at_price: v.compare_at_price ?? null,
        options: Array.isArray(v.options) ? v.options : [],
      }));
    } else if (productRaw) {
      this.optionNames = optionNamesFrom(productRaw.options);
      this.variants = (productRaw.variants || []).map((v) =>
        this.#normalizeLegacyVariant(v)
      );
    } else {
      return;
    }

    this.optionKeyMap = buildOptionKeyMap(this.optionNames);
    this.activeOptionKeys = UI_OPTION_KEYS.filter((uiKey) =>
      this.optionNames.some(
        (n) => String(n).trim().toLowerCase() === uiKey.toLowerCase()
      )
    );
    this.currency = this.dataset.currency || 'USD';
    this.galleryMap = this.#parseGalleryMap();

    this.selected = {
      Color: this.dataset.defaultColor || DEFAULTS.Color,
      Size: this.dataset.defaultSize || DEFAULTS.Size,
    };
    if (this.activeOptionKeys.includes('Pack')) {
      this.selected.Pack = normalizePack(this.dataset.defaultPack || '3 pairs');
    }

    this.#cacheEls();
    this.#bind();
    // Portal after bind so sheet escapes .hoof-product { isolation: isolate }
    // (and sibling section stacking contexts) that trap position:fixed z-index.
    this.#portalSheet();
    this.#ensureSelectedSizeAvailable();
    this.#sync();

    this.#onOpenPurchaseSheet = () => this.openSheet();
    // Only the first buy UI on the page listens for the global sticky/hero event.
    const allBuys = document.querySelectorAll('hoof-product-buy');
    if (allBuys[0] === this) {
      window.addEventListener('hoof:open-purchase-sheet', this.#onOpenPurchaseSheet);
    }
    this.addEventListener('hoof:open-local-sheet', this.#onOpenPurchaseSheet);
  }

  disconnectedCallback() {
    if (this.#onOpenPurchaseSheet) {
      window.removeEventListener('hoof:open-purchase-sheet', this.#onOpenPurchaseSheet);
    }
    // Remove body-portaled sheet when this buy UI unmounts (avoid orphans).
    if (this.els?.sheet?.parentElement === document.body) {
      this.els.sheet.remove();
    }
    if (this.els?.sheet?.classList.contains('is-open')) {
      document.body.style.overflow = '';
    }
  }

  #ready = false;
  /** @type {(() => void) | undefined} */
  #onOpenPurchaseSheet;

  #parseProductJson() {
    const raw = this.querySelector('[data-hoof-product-json]')?.textContent;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  #parseVariantIndex() {
    const raw = this.querySelector('[data-hoof-variant-index]')?.textContent;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  #parseGalleryMap() {
    const node = this.querySelector('[data-hoof-gallery-json]');
    if (!node?.textContent) return {};
    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  /** Fallback when only product|json is present. */
  #normalizeLegacyVariant(v) {
    const names = this.optionNames;
    let values = [];

    if (Array.isArray(v.options) && v.options.length) {
      values = v.options.map((val) => String(val));
    } else {
      values = names.map((_, i) => {
        const key = `option${i + 1}`;
        return v[key] != null ? String(v[key]) : '';
      });
    }

    if (
      (!values.length || values.every((x) => !x)) &&
      Array.isArray(v.selected_options)
    ) {
      const byName = {};
      v.selected_options.forEach((o) => {
        if (o?.name) byName[o.name] = o.value;
      });
      values = names.map((name) =>
        byName[name] != null ? String(byName[name]) : ''
      );
    }

    return {
      id: v.id,
      available: isVariantAvailable(v),
      price: v.price,
      compare_at_price: v.compare_at_price ?? null,
      options: values,
    };
  }

  #cacheEls() {
    this.els = {
      priceNodes: this.querySelectorAll('[data-hoof-price]'),
      compareNodes: this.querySelectorAll('[data-hoof-compare]'),
      saleBadges: this.querySelectorAll('[data-hoof-sale-badge]'),
      errorNodes: this.querySelectorAll('[data-hoof-error]'),
      variantInput: this.querySelector('[data-hoof-variant-id]'),
      buyButtons: this.querySelectorAll('[data-hoof-add]'),
      sheet: this.querySelector('[data-hoof-sheet]'),
      sheetOpeners: this.querySelectorAll('[data-hoof-open-sheet]'),
      sheetClosers: this.querySelectorAll('[data-hoof-close-sheet]'),
      mainImage: this.querySelector('[data-hoof-gallery-main]'),
      duo: this.querySelector('[data-hoof-duo]'),
      thumbs: this.querySelectorAll('[data-hoof-gallery-thumb]'),
      previewImages: this.querySelectorAll('[data-hoof-sheet-preview]'),
      colorButtons: this.querySelectorAll('[data-hoof-option="Color"]'),
      packButtons: this.querySelectorAll('[data-hoof-option="Pack"]'),
      sizeButtons: this.querySelectorAll('[data-hoof-option="Size"]'),
    };
  }

  #setMainImage(image) {
    if (!this.els.mainImage || !image?.src) return;
    this.els.mainImage.hidden = false;
    this.els.mainImage.src = image.src;
    this.els.mainImage.alt = image.alt || '';
    if (image.width) this.els.mainImage.width = Number(image.width) || this.els.mainImage.width;
    if (image.height) this.els.mainImage.height = Number(image.height) || this.els.mainImage.height;
  }

  #showGalleryThumb(thumb) {
    if (!thumb || this.dataset.hoofGalleryLocked === 'true') return;
    this.#setMainImage({
      src: thumb.dataset.src,
      alt: thumb.dataset.alt || '',
      width: thumb.dataset.width,
      height: thumb.dataset.height,
    });
    this.els.thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
  }

  /**
   * Move the mobile purchase sheet to document.body so fixed positioning /
   * z-index are not trapped by ancestor stacking contexts
   * (e.g. .hoof-product { isolation: isolate }).
   */
  #portalSheet() {
    const sheet = this.els?.sheet;
    if (!sheet) return;

    document.querySelectorAll('body > .hoof-sheet').forEach((el) => {
      if (el !== sheet) el.remove();
    });

    if (sheet.parentElement !== document.body) {
      document.body.appendChild(sheet);
    }
  }

  #bind() {
    this.els.colorButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.selected.Color = btn.dataset.value;
        this.#ensureSelectedSizeAvailable();
        this.#sync();
      });
    });
    this.els.packButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.selected.Pack = normalizePack(btn.dataset.value);
        this.#ensureSelectedSizeAvailable();
        this.#sync();
      });
    });
    this.els.sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-oos') || btn.disabled) return;
        this.selected.Size = btn.dataset.value;
        this.#sync();
      });
    });
    this.els.buyButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (btn.dataset.sheetConfirm === 'true') {
          this.addToCart().then((ok) => {
            if (ok) this.closeSheet();
          });
        } else if (btn.dataset.openSheet === 'true') {
          this.openSheet();
        } else if (btn.dataset.hoofBuyNow === 'true') {
          this.addToCart({ checkout: true });
        } else {
          this.addToCart();
        }
      });
    });
    this.els.sheetOpeners.forEach((btn) => {
      btn.addEventListener('click', () => this.openSheet());
    });
    this.els.sheetClosers.forEach((btn) => {
      btn.addEventListener('click', () => this.closeSheet());
    });
    this.els.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.#showGalleryThumb(thumb);
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeSheet();
    });
  }

  /** Map variant.options[] → { [optionName]: normalizedValue } using actual option order. */
  #variantOptions(v) {
    const map = {};
    const names = this.optionNames;
    const values = Array.isArray(v.options) ? v.options : [];
    names.forEach((name, i) => {
      if (values[i] == null || values[i] === '') return;
      map[name] = normalizeOptionValue(name, values[i]);
    });
    return map;
  }

  #selectionValue(uiKey, overrides = {}) {
    if (uiKey === 'Pack') {
      return normalizePack(overrides.Pack ?? this.selected.Pack);
    }
    return overrides[uiKey] ?? this.selected[uiKey];
  }

  #matchesSelection(v, overrides = {}) {
    const opts = this.#variantOptions(v);
    const keys = this.activeOptionKeys || UI_OPTION_KEYS;
    return keys.every((uiKey) => {
      const optionName = this.optionKeyMap[uiKey];
      const got = opts[optionName];
      const want = this.#selectionValue(uiKey, overrides);
      return got === want;
    });
  }

  #findVariant(overrides = {}) {
    return (this.variants || []).find((v) => this.#matchesSelection(v, overrides));
  }

  #sizeAvailable(size) {
    return (this.variants || []).some(
      (v) => this.#matchesSelection(v, { Size: size }) && isVariantAvailable(v)
    );
  }

  /** After color/pack change, move off an OOS size onto the first available size. */
  #ensureSelectedSizeAvailable() {
    if (!this.activeOptionKeys?.includes('Size')) return;
    if (this.#sizeAvailable(this.selected.Size)) return;
    const preferred = ['M', 'L', 'S'];
    const next = preferred.find((size) => this.#sizeAvailable(size));
    if (next) this.selected.Size = next;
  }

  #sync() {
    const variant = this.#findVariant();
    const canAdd = isVariantAvailable(variant);

    if (this.els.variantInput && variant) {
      this.els.variantInput.value = String(variant.id);
    }

    const sale = saleMeta(variant);
    this.els.priceNodes.forEach((el) => {
      el.textContent = variant ? money(variant.price, this.currency) : '—';
    });
    this.els.compareNodes.forEach((el) => {
      if (sale.onSale) {
        el.textContent = money(sale.compare, this.currency);
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    });
    this.els.saleBadges.forEach((el) => {
      if (sale.onSale && sale.savePct > 0) {
        el.textContent = `${sale.savePct}% OFF`;
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    });

    this.els.colorButtons.forEach((btn) => {
      const on = btn.dataset.value === this.selected.Color;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', String(on));
    });
    this.els.packButtons.forEach((btn) => {
      const val = normalizePack(btn.dataset.value);
      const on = val === this.selected.Pack;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', String(on));
    });
    this.els.sizeButtons.forEach((btn) => {
      const size = btn.dataset.value;
      const available = this.#sizeAvailable(size);
      const active = size === this.selected.Size && available;
      btn.classList.toggle('is-active', active);
      btn.classList.toggle('is-oos', !available);
      btn.disabled = !available;
      btn.setAttribute('aria-pressed', String(active));
      btn.setAttribute('aria-disabled', String(!available));
      const meta = btn.querySelector('[data-hoof-size-meta]');
      if (meta) {
        meta.textContent = available ? SIZE_RANGES[size] || '' : 'Out of Stock';
      }
      btn.setAttribute(
        'aria-label',
        available ? `${size}, ${SIZE_RANGES[size] || ''}` : `${size}, Out of Stock`
      );
    });

    this.els.buyButtons.forEach((btn) => {
      if (btn.dataset.openSheet === 'true') {
        btn.disabled = false;
      } else {
        btn.disabled = !canAdd;
      }
    });

    this.els.errorNodes.forEach((el) => {
      el.hidden = true;
      el.textContent = '';
    });

    this.#updateGalleryForColor();
  }

  #updateGalleryForColor() {
    // Starter Kit (and any locked card): keep the Liquid-selected hero; ignore size/color sync.
    if (this.dataset.hoofGalleryLocked === 'true') {
      this._galleryInitialized = true;
      return;
    }

    const hasColor = this.activeOptionKeys.includes('Color');
    const galleryKey = hasColor
      ? this.selected.Color || 'Pure White'
      : 'Default';

    // Avoid resetting the active image when only size/availability syncs.
    if (this._galleryKey === galleryKey && this._galleryInitialized) {
      return;
    }

    const images =
      this.galleryMap[galleryKey] ||
      this.galleryMap.Default ||
      this.galleryMap['Pure White'] ||
      this.galleryMap.White ||
      [];
    if (!images.length) return;

    this._galleryKey = galleryKey;
    this._galleryInitialized = true;

    this.#setMainImage(images[0]);
    this.els.previewImages.forEach((img) => {
      img.src = images[0].src;
      img.alt = '';
    });

    this.els.thumbs.forEach((thumb, i) => {
      const image = images[i];
      if (!image) {
        thumb.hidden = true;
        return;
      }
      thumb.hidden = false;
      thumb.dataset.src = image.src;
      thumb.dataset.alt = image.alt || '';
      if (image.width) thumb.dataset.width = String(image.width);
      if (image.height) thumb.dataset.height = String(image.height);
      if (image.alt) {
        thumb.setAttribute('aria-label', image.alt);
      }
      const img = thumb.querySelector('img');
      if (img) {
        img.src = image.thumb || image.src;
        img.alt = '';
      }
      thumb.classList.toggle('is-active', i === 0);
    });
  }

  openSheet() {
    if (!this.els.sheet) return;
    this.#portalSheet();
    this.els.sheet.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeSheet() {
    if (!this.els.sheet) return;
    this.els.sheet.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /**
   * @param {{ checkout?: boolean }} [opts]
   * checkout:true uses native Shopify cart add, then redirects to /checkout (Buy Now).
   */
  async addToCart(opts = {}) {
    const variant = this.#findVariant();
    if (!isVariantAvailable(variant)) {
      this.els.errorNodes.forEach((el) => {
        el.hidden = false;
        el.textContent = 'This combination is unavailable.';
      });
      return false;
    }

    const formData = new FormData();
    formData.append('id', String(variant.id));
    formData.append('quantity', '1');

    const cartAddUrl =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
      '/cart/add.js';

    let deferredResolve;
    let deferredReject;
    const promise = new Promise((resolve, reject) => {
      deferredResolve = resolve;
      deferredReject = reject;
    });

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise,
      })
    );

    try {
      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const data = await res.json();
      if (data.status) {
        document.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferredReject?.(new Error(data.message || 'Add to cart failed'));
        this.els.errorNodes.forEach((el) => {
          el.hidden = false;
          el.textContent = data.description || data.message || 'Could not add to cart.';
        });
        return false;
      }

      const cartRes = await fetch((window.Theme?.routes?.cart_url || '/cart') + '.js');
      const cart = await cartRes.json();
      deferredResolve?.({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse
          ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
          : cart,
        detail: { didError: false, items: cart.items, source: 'hoof-product-buy' },
      });

      if (opts.checkout) {
        window.location.href = '/checkout';
        return true;
      }

      document.querySelector('[aria-controls="cart-drawer"]')?.click();
      return true;
    } catch (err) {
      deferredReject?.(err);
      this.els.errorNodes.forEach((el) => {
        el.hidden = false;
        el.textContent = 'Could not add to cart. Please try again.';
      });
      return false;
    }
  }
}

if (!customElements.get('hoof-product-buy')) {
  customElements.define('hoof-product-buy', HoofProductBuy);
}

export { normalizePack };
